import type {
  SystemStyleObject
} from '@chakra-ui/react'
import {
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import {
  CustomLinkOverlay,
  EmptyTablePlaceholder,
  TableSkeleton
} from 'BrokerTsLib/src/components'
import type {
  ColumnDefinition,
  ColumnsDefinition,
} from 'BrokerTsLib/src/interfaces/columnsDefinition'
import { useContext } from 'react'
import { Link as RouterLink } from 'react-router'

import { TableWrapper, ValidityBadge } from '@/components'
import { ppzAuthorizationListContext } from '@/context/PpzAuthorizationListContext'
import type { PpzAuthorizationGridItem } from '@/dtos'
import { formatDate } from '@/utils/formatDate'

export const PpzAuthorizationListIdColumn: ColumnDefinition<PpzAuthorizationGridItem> =
{
  key: 'id',
  name: 'ID',
  cellExpr: (item) => <Link as={RouterLink} to={`/ppzAuthorization/${item.id}`} color='primary.500'>{item.id}</Link>,
}

export const PpzAuthorizationListUserColumn: ColumnDefinition<PpzAuthorizationGridItem> =
{
  key: 'user',
  name: 'Uživatel',
  cellExpr: (item) => <Text>{item.ppzUser}</Text>,
}

export const PpzAuthorizationListCreatedColumn: ColumnDefinition<PpzAuthorizationGridItem> =
{
  key: 'created',
  name: 'Datum vytvoření',
  cellExpr: (item) => item.dateCreated && formatDate(item.dateCreated),
}

export const PpzAuthorizationListValidFromColumn: ColumnDefinition<PpzAuthorizationGridItem> =
{
  key: 'validFrom',
  name: 'Platnost od',
  cellExpr: (item) => formatDate(item.authorizationValidFrom),
}

export const PpzAuthorizationListValidToColumn: ColumnDefinition<PpzAuthorizationGridItem> =
{
  key: 'validTo',
  name: 'Platnost do',
  cellExpr: (item) => formatDate(item.authorizationValidTo),
}

export const PpzAuthorizationListApprovedDateColumn: ColumnDefinition<PpzAuthorizationGridItem> =
{
  key: 'approvedDate',
  name: 'Potvrzeno dne',
  cellExpr: (item) => item.approvedDate && formatDate(item.approvedDate),
}

export const PpzAuthorizationListFilesCountColumn: ColumnDefinition<PpzAuthorizationGridItem> =
{
  key: 'filesCount',
  name: 'Počet souborů',
  cellExpr: (item) => item.filesCount > 0 ? item.filesCount : '-',
  cellProps: { textAlign: 'right' },
  headerCellProps: { textAlign: 'right' },
}

export const PpzAuthorizationListValidColumn: ColumnDefinition<PpzAuthorizationGridItem> =
{
  key: 'valid',
  name: 'Platnost',
  cellExpr: (item) => <ValidityBadge valid={item.valid} showValid />,
  cellProps: { textAlign: 'right', justifyItems: 'right' },
  headerCellProps: { textAlign: 'right' },
}

export const PpzAuthorizationListDefaultColumns: ColumnsDefinition<PpzAuthorizationGridItem> =
  [
    PpzAuthorizationListIdColumn,
    PpzAuthorizationListUserColumn,
    PpzAuthorizationListCreatedColumn,
    PpzAuthorizationListValidFromColumn,
    PpzAuthorizationListValidToColumn,
    PpzAuthorizationListApprovedDateColumn,
    PpzAuthorizationListFilesCountColumn,
    PpzAuthorizationListValidColumn,
  ]

export const PpzAuthorizationList = ({
  ppzAuthorizations,
  sx,
  columnsDefinition = PpzAuthorizationListDefaultColumns,
}: {
  ppzAuthorizations: Array<PpzAuthorizationGridItem>
  sx?: SystemStyleObject
  columnsDefinition?: ColumnsDefinition<PpzAuthorizationGridItem>
}) => {
  const { pageSize, loading } = useContext(ppzAuthorizationListContext)
  return (
    <TableWrapper>
      <Table variant="simple" sx={sx}>
        <Thead>
          <Tr>
            {columnsDefinition &&
              columnsDefinition.map((column) => (
                <Th
                  key={column.key}
                  _empty={{ w: 0, p: 0 }}
                  {...column.headerCellProps}
                >
                  {column.name}
                </Th>
              ))}
          </Tr>
        </Thead>

        <Tbody>
          <TableSkeleton
            rowsCount={pageSize}
            colSpan={columnsDefinition.length}
            isLoaded={!loading}
          >
            <EmptyTablePlaceholder
              text={'Žádné autorizace PPZ k zobrazení'}
              colSpan={columnsDefinition.length}
            >
              {ppzAuthorizations.map((ppzAuthorization) => {

                const rowBg = (!ppzAuthorization.valid)
                  ? (ppzAuthorization.approvedDate ? 'red.50' : '')
                  : (ppzAuthorization.valid ? 'green.50' : 'none')

                return (
                  <CustomLinkOverlay key={ppzAuthorization.id} to={`/ppzAuthorization/${ppzAuthorization.id}`} as={Tr} background={rowBg}>
                    {columnsDefinition &&
                      columnsDefinition.map((column) => (
                        <Td
                          key={column.key}
                          _empty={{ p: 0 }}
                          {...column.cellProps}
                        >
                          {column.cellExpr(ppzAuthorization) || ''}
                        </Td>
                      ))}
                  </CustomLinkOverlay>
                )
              })}
            </EmptyTablePlaceholder>
          </TableSkeleton>
        </Tbody>
      </Table>
    </TableWrapper>
  )
}
